import React, { FormEvent, useState, useRef, useEffect } from "react";
import { FaChevronDown, FaSearch } from "react-icons/fa";
import { useTheme } from "src/context/theme.context";

type SelectOption = {
  value: string;
  text: string;
};

export type SearchItemConfig = {
  type: "input" | "select";
  placeholder: string;
  options?: SelectOption[];
};

export type ActiveSearchFilter<T extends string> = {
  key: T;
  label: string;
  value: string;
};

interface Props<T extends string> {
  handleSubmit: (type: T, input: string) => void;
  searchItems: Record<T, SearchItemConfig>;
  activeFilters?: ActiveSearchFilter<T>[];
  onRemoveFilter?: (type: T) => void;
  onClearAll?: () => void;
}

export const SearchDropdownForm = <T extends string>({
  handleSubmit,
  searchItems,
  activeFilters = [],
  onRemoveFilter,
  onClearAll,
}: Props<T>) => {
  const { isDarkMode } = useTheme();
  const items = Object.keys(searchItems) as T[];
  const [selected, setSelected] = useState<T>(items[0]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentConfig = searchItems[selected];

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Reset query when switching between different search types
  useEffect(() => {
    setQuery("");
  }, [selected]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleSubmit(selected, query.trim());
  };

  const renderInputField = () => {
    if (currentConfig.type === "select" && currentConfig.options) {
      return (
        <select
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`relative z-10 w-full px-6 py-4 bg-transparent focus:outline-none text-lg border-none ${
            isDarkMode ? "text-slate-100" : "text-slate-800"
          }`}
        >
          <option value="">{currentConfig.placeholder}</option>
          {currentConfig.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        id="search-dropdown"
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={currentConfig.placeholder}
        className={`relative z-10 w-full px-6 py-4 bg-transparent focus:border-none focus:outline-none focus:ring-0 text-lg flex-grow border-none ${
          isDarkMode ? "text-slate-100 placeholder-slate-400" : "text-slate-800 placeholder-slate-400"
        }`}
      />
    );
  };

  return (
    <div className="from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="w-full max-w-4xl mx-auto">
        <form onSubmit={onSubmit}>
        <div ref={ref} className="relative group">
          <label htmlFor="search-dropdown" className="sr-only">
            Search category
          </label>

          {/* Main Search Container */}
          <div
            className={`
              flex rounded-2xl overflow-hidden transition-all duration-300 ease-out
              backdrop-blur-sm border-2 shadow-lg
              ${
                focused || open
                  ? `${isDarkMode ? "bg-slate-900/95 border-blue-400 shadow-xl shadow-blue-950/50" : "bg-white/90 border-blue-400 shadow-xl shadow-blue-100/50"} scale-[1.02]`
                  : `${isDarkMode ? "bg-slate-900/95 border-slate-700 hover:border-slate-500 hover:shadow-xl" : "bg-white/90 border-slate-200 hover:border-slate-300 hover:shadow-xl"}`
              }
            `}
          >
            {/* Category Dropdown Button */}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className={`
                  flex items-center px-6 py-4 bg-gradient-to-r transition-all duration-200
                  ${
                    open
                      ? "from-blue-500 to-indigo-500 text-white"
                      : isDarkMode
                      ? "from-slate-800 to-slate-800 text-slate-100 hover:from-slate-700 hover:to-slate-800"
                      : "from-slate-100 to-slate-50 text-slate-700 hover:from-slate-200 hover:to-slate-100"
                  }
                  ${isDarkMode ? "border-r border-slate-700" : "border-r border-slate-200"} font-medium 
                `}
            >
              <span className="truncate">{selected}</span>
              <FaChevronDown
                className={`
                  ml-3 w-3 h-3 transition-transform duration-200
                  ${open ? "rotate-180" : ""}
                `}
              />
            </button>

            {/* Search Input/Select */}
            <div className="flex-1 relative">
              {renderInputField()}

              {/* Input glow effect */}
              {focused && (
                <div
                  className={`absolute inset-0 z-0 pointer-events-none border-none ${
                    isDarkMode ? "bg-gradient-to-r from-blue-500/5 to-indigo-500/5" : "bg-gradient-to-r from-blue-50/30 to-indigo-50/30"
                  }`}
                />
              )}
            </div>

            {/* Search Button */}
            <button
              type="submit"
              className={`
                  px-8 py-4 transition-all duration-200 font-semibold flex items-center justify-center flex-shrink-0
                  ${
                    query.trim()
                      ? "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                      : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
                  }
                `}
            >
              <FaSearch className="w-5 h-5" />
              <span className="sr-only">Search</span>
            </button>
          </div>

          {/* Dropdown Menu */}
          <div
            className={`
              absolute top-full left-0 mt-2 z-50 transition-all duration-300 ease-out origin-top w-auto
              backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden
              ${isDarkMode ? "bg-slate-900/95 border border-slate-700" : "bg-white/95 border border-slate-200"}
              ${open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"}
            `}
          >
            <ul className="pl-0 list-none m-0 text-sm">
              {items.map((item, index) => (
                <li key={item}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(item);
                      setOpen(false);
                    }}
                    className={`
                        w-full px-20 py-3 text-left transition-all duration-150 flex items-center block
                        ${
                          selected === item
                            ? isDarkMode
                              ? "bg-gradient-to-r from-blue-500/20 to-indigo-500/20 text-blue-200 font-medium border-r-4 border-blue-400"
                              : "bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 font-medium border-r-4 border-blue-400"
                            : isDarkMode
                            ? "text-slate-200 hover:bg-slate-800"
                            : "text-slate-700 hover:bg-slate-50"
                        }
                      `}
                    style={{
                      animationDelay: `${index * 50}ms`,
                    }}
                  >
                    <span className="flex-1">{item}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        </form>

        {activeFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {activeFilters.map((filter) => (
              <button
                key={`${filter.key}-${filter.value}`}
                type="button"
                onClick={() => onRemoveFilter?.(filter.key)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition-colors ${
                  isDarkMode
                    ? "border-blue-500/30 bg-blue-500/10 text-blue-100 hover:bg-blue-500/15"
                    : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                }`}
                title={`Remove ${filter.label}`}
              >
                <span className="font-medium">{filter.label}:</span>
                <span>{filter.value}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}

            {onClearAll && activeFilters.length > 1 && (
              <button
                type="button"
                onClick={onClearAll}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  isDarkMode
                    ? "bg-slate-800 text-slate-200 hover:bg-slate-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Clear All
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
